import type { AgentConfiguratorState } from '../types/agentConfigurator';
import type { DatasetGenerationPredictTokenUsageRequest } from '../types/datasetGenerationPreview';
import type { VirtualUser } from '../types/vuser';
import {
  buildVirtualUserUserTypePromptApiPayload,
} from '../constants/vuserDefaults';

function parseJsonObject(raw: unknown): Record<string, unknown> | null {
  if (raw == null) return null;
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as Record<string, unknown>;
  }
  if (typeof raw !== 'string' || !raw.trim()) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed != null && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

export function wrapAssistantConfigEnvelope(core: Record<string, unknown>): Record<string, unknown> {
  return { full_config: { config: core } };
}

export function buildAssistantConfigFromConfigurator(
  config: AgentConfiguratorState
): Record<string, unknown> {
  const core: Record<string, unknown> = {
    coordinator: config.coordinator,
    workers: config.workers,
    metaPrompts: config.metaPrompts,
  };
  if (config.async_orchestrator) core.async_orchestrator = config.async_orchestrator;
  if (config.reminder_prompt_position) {
    core.reminder_prompt_position = config.reminder_prompt_position;
  }
  if (config.approach) core.approach = config.approach;
  return wrapAssistantConfigEnvelope(core);
}

/** Parses GET /connector/.../config into assistant_config payload for ML predict. */
export function buildAssistantConfigPayloadFromIntegration(raw: unknown): Record<string, unknown> | null {
  const d = (raw as { data?: unknown })?.data ?? raw;
  const root = d as Record<string, unknown> | null;
  if (!root) return null;

  const configNode =
    (root.config as Record<string, unknown> | undefined) ??
    ((root.data as Record<string, unknown> | undefined)?.config as Record<string, unknown> | undefined) ??
    (root.data as Record<string, unknown> | undefined);

  if (!configNode) return null;

  const nested =
    (configNode.config as Record<string, unknown> | undefined)?.config ??
    configNode.config ??
    configNode;

  if (typeof nested === 'string') {
    const parsed = parseJsonObject(nested);
    return parsed ? wrapAssistantConfigEnvelope(parsed) : null;
  }

  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const obj = nested as Record<string, unknown>;
    if (obj.coordinator || Array.isArray(obj.workers)) {
      return wrapAssistantConfigEnvelope(obj);
    }
    if (obj.full_config) return obj;
  }

  return null;
}

export function toVuserConfigApiEntry(vu: VirtualUser): Record<string, unknown> {
  const parsed = parseJsonObject(vu.user_config);
  const base =
    parsed?.user_config && typeof parsed.user_config === 'object' && !Array.isArray(parsed.user_config)
      ? parsed
      : { user_config: parsed ?? {} };

  if (vu.type === 'user') {
    return {
      ...base,
      ...buildVirtualUserUserTypePromptApiPayload(vu),
    };
  }

  if (!parsed) return { user_config: {} };
  if (parsed.user_config && typeof parsed.user_config === 'object' && !Array.isArray(parsed.user_config)) {
    return parsed;
  }
  return { user_config: parsed };
}

export function buildVuserConfigPayload(
  virtualUsers: VirtualUser[],
  ids: string[]
): Record<string, unknown>[] {
  const uniqueIds = Array.from(new Set(ids.filter(Boolean)));
  return uniqueIds
    .map((id) => virtualUsers.find((u) => u.id === id))
    .filter((u): u is VirtualUser => Boolean(u))
    .map(toVuserConfigApiEntry);
}

export function buildDatasetGenerationPredictTokenUsageRequest(params: {
  assistant_config: object | string;
  vuser_config?: object | object[];
  vadmin_config?: object | object[];
  dialog_count: number;
  message_count: number;
}): DatasetGenerationPredictTokenUsageRequest {
  const body: DatasetGenerationPredictTokenUsageRequest = {
    assistant_config: params.assistant_config,
    dialog_count: params.dialog_count,
    message_count: params.message_count,
  };

  if (params.vuser_config != null) {
    body.vuser_config = params.vuser_config;
  }
  if (params.vadmin_config != null) {
    body.vadmin_config = params.vadmin_config;
  }

  return body;
}
